import React, { useState } from 'react'
import { Col, Dropdown, Row, Tabs } from 'antd'
import { PlusCircle } from '@phosphor-icons/react'
import AgeRecepient from './AgeRecepient'
import GenderRecepient from './GenderRecepient'
import TicketsRecepient from './TicketsRecepient'
import DateRecepient from './DateRecepient'
import RecepientInputTab from './RecepientInputTab'
import { RecepientToSend } from 'types/event/eventStep5'
import '../styles/styles.scss'
import { capitalizeFirstLetter } from 'helper/utility'

interface RecepientProps {
  recepientPayload: RecepientToSend
  setRecepientPayload: React.Dispatch<React.SetStateAction<RecepientToSend>>
  reference?: 'event' | 'league' | 'organization' | 'class'
}

const Recepient: React.FC<RecepientProps> = ({
  recepientPayload,
  setRecepientPayload,
  reference,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [activeTab, setActiveTab] = useState('1')

  const {
    minAge = 0,
    maxAge = 0,
    gender = undefined,
    admissionTicketIds = [],
    additionalOptionIds = [],
    dropInTicketIds = [],
    eventDates = [],
  } = recepientPayload || {}

  const hasTickets =
    admissionTicketIds.length > 0 ||
    additionalOptionIds.length > 0 ||
    dropInTicketIds.length > 0

  const hasRecepients =
    gender || hasTickets || eventDates.length > 0 || minAge || maxAge

  const handleClear = () => {
    if (!recepientPayload) return

    switch (activeTab) {
      case '1':
        setRecepientPayload({
          ...recepientPayload,
          minAge: 0,
          maxAge: 0,
        })
        break
      case '2':
        setRecepientPayload({
          ...recepientPayload,
          gender: undefined,
        })
        break
      case '3':
        setRecepientPayload({
          ...recepientPayload,
          admissionTicketIds: [],
          additionalOptionIds: [],
          dropInTicketIds: [],
        })
        break
      case '4':
        setRecepientPayload({
          ...recepientPayload,
          eventDates: [],
        })
        break
    }
  }

  const handleApply = () => {
    setIsOpen(false)
  }

  const getAgeTagText = (minAge?: number, maxAge?: number) => {
    if (minAge === 0 && maxAge === 0) return 'All Ages'
    if (minAge && !maxAge) return `Ages ${minAge}+`
    if (!minAge && maxAge) return `Ages up to ${maxAge}`
    if (minAge && maxAge) return `Ages ${minAge}-${maxAge}`
    return 'All Ages'
  }

  const handleChipClick = (tabKey: string) => {
    setActiveTab(tabKey)
    setIsOpen(true)
  }

  const handleInputClick = () => {
    setActiveTab('1') // Set to first tab (Age)
    setIsOpen(true)
  }

  return (
    <>
      <Row gutter={10}>
        <Col span={24}>
          <label className="input-label">Recipients</label>
        </Col>

        <div className="w-full">
          <Dropdown
            dropdownRender={menu => (
              <div
                style={{
                  minWidth: '620px',
                  height: '400px',
                  overflowY: 'auto',
                  background: '#fff',
                  borderRadius: '20px',
                  boxShadow: 'rgba(0, 0, 0, 0.35) 0px 5px 15px',
                }}
              >
                <div className="px-20px py-10px flex flex-col gap-20px">
                  <div className="flex-1">
                    <Tabs
                      activeKey={activeTab}
                      onChange={setActiveTab}
                      items={[
                        {
                          label: `Age`,
                          key: '1',
                          children: (
                            <div className="">
                              <AgeRecepient
                                recepientPayload={recepientPayload}
                                setRecepientPayload={setRecepientPayload}
                              />
                            </div>
                          ),
                        },
                        {
                          label: `Gender`,
                          key: '2',
                          children: (
                            <div>
                              <GenderRecepient
                                recepientPayload={recepientPayload}
                                setRecepientPayload={setRecepientPayload}
                              />
                            </div>
                          ),
                        },
                        ...(reference === 'event'
                          ? [
                              {
                                label: `Tickets`,
                                key: '3',
                                children: (
                                  <TicketsRecepient
                                    recepientPayload={recepientPayload}
                                    setRecepientPayload={setRecepientPayload}
                                  />
                                ),
                              },
                            ]
                          : []),
                        {
                          label: `Days`,
                          key: '4',
                          children: (
                            <div>
                              <DateRecepient
                                recepientPayload={recepientPayload}
                                setRecepientPayload={setRecepientPayload}
                                reference={reference}
                              />
                            </div>
                          ),
                        },
                      ]}
                    />
                  </div>
                  <div className="flex gap-20px align-center justify-between">
                    <button
                      className="button-modal clear"
                      onClick={handleClear}
                    >
                      Clear
                    </button>
                    <button className="button-modal prim" onClick={handleApply}>
                      Apply
                    </button>
                  </div>
                </div>
              </div>
            )}
            placement="bottomLeft"
            trigger={['click']}
            className="recepient-input-block"
            open={isOpen}
            onOpenChange={setIsOpen}
          >
            <Col style={{ height: '50px' }}>
              <div
                className="w-full border-1px radius-10 flex align-center justify-between px-12px pointer h-full"
                onClick={handleInputClick}
              >
                {hasRecepients ? (
                  <div
                    className="flex items-center gap-20px"
                    onClick={e => e.stopPropagation()}
                  >
                    {minAge || maxAge ? (
                      <RecepientInputTab
                        text={getAgeTagText(minAge, maxAge)}
                        onClick={() => handleChipClick('1')}
                      />
                    ) : null}
                    {gender && (
                      <RecepientInputTab
                        text={capitalizeFirstLetter(gender)}
                        onClick={() => handleChipClick('2')}
                      />
                    )}
                    {hasTickets && (
                      <RecepientInputTab
                        text="Tickets"
                        onClick={() => handleChipClick('3')}
                      />
                    )}
                    {eventDates.length > 0 && (
                      <RecepientInputTab
                        text={`Days(${eventDates.length})`}
                        onClick={() => handleChipClick('4')}
                      />
                    )}
                  </div>
                ) : (
                  <p className="text-gray">Select recipients</p>
                )}
                <PlusCircle size={20} color="#000" />
              </div>
            </Col>
          </Dropdown>
        </div>
      </Row>
    </>
  )
}

export default Recepient
